import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity} from 'react-native';
import {
  Text, TextInput, Button, Appbar, Snackbar, ActivityIndicator,
  Chip, Dialog, Portal, Divider, Switch,
} from 'react-native-paper';
import {launchImageLibrary} from 'react-native-image-picker';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useForm, Controller} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {workerApi} from '../../services/api/workerApi';
import {workerTypeApi} from '../../services/api/workerTypeApi';
import {orgUnitApi} from '../../services/api/orgUnitApi';
import {roleApi} from '../../services/api/roleApi';
import {useAuthStore} from '../../store/useAuthStore';
import PhoneInput from '../../components/common/PhoneInput';
import {colors, spacing} from '../../theme';

const EMPLOYMENT_TYPES = [
  {value: 'FULL_TIME', label: 'Full Time'},
  {value: 'PART_TIME', label: 'Part Time'},
  {value: 'CONTRACT', label: 'Contract'},
  {value: 'FREELANCER', label: 'Freelancer'},
  {value: 'DAILY_WAGE', label: 'Daily Wage'},
  {value: 'PIECE_RATE', label: 'Piece Rate'},
  {value: 'COMMISSION_BASED', label: 'Commission Based'},
  {value: 'OUTSOURCED', label: 'Outsourced'},
  {value: 'TEMPORARY', label: 'Temporary'},
  {value: 'INTERN', label: 'Intern'},
];

const WorkerFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const workerId = route.params?.workerId;
  const isEditing = !!workerId;

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const tenantCountryCode = useAuthStore(s => s.user?.tenant?.countryCode) || '+91';
  const [countryCode, setCountryCode] = useState(tenantCountryCode);
  const [avatarImage, setAvatarImage] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Lookup data
  const [workerTypes, setWorkerTypes] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [roles, setRoles] = useState([]);

  // Selection state
  const [selectedWorkerTypeId, setSelectedWorkerTypeId] = useState(null);
  const [selectedEmploymentType, setSelectedEmploymentType] = useState(null);
  const [selectedOrgUnitIds, setSelectedOrgUnitIds] = useState([]);
  const [selectedPrimaryOrgId, setSelectedPrimaryOrgId] = useState(null);

  // User account state
  const [createUserAccount, setCreateUserAccount] = useState(false);
  const [hasExistingUser, setHasExistingUser] = useState(false);
  const [password, setPassword] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  // Dialogs
  const [workerTypeDialogVisible, setWorkerTypeDialogVisible] = useState(false);
  const [empTypeDialogVisible, setEmpTypeDialogVisible] = useState(false);
  const [orgUnitDialogVisible, setOrgUnitDialogVisible] = useState(false);
  const [primaryOrgDialogVisible, setPrimaryOrgDialogVisible] = useState(false);
  const [roleDialogVisible, setRoleDialogVisible] = useState(false);

  const schema = yup.object().shape({
    fullName: yup.string().required(t('validation.required')),
    workerCode: yup.string(),
    phone: yup.string(),
    email: yup.string().email(t('validation.email')).nullable(),
    notes: yup.string(),
  });

  const {control, handleSubmit, reset, formState: {errors}} = useForm({
    resolver: yupResolver(schema),
    defaultValues: {fullName: '', workerCode: '', phone: '', email: '', notes: ''},
  });

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [wtRes, ouRes, roleRes] = await Promise.all([
          workerTypeApi.listWorkerTypes(),
          orgUnitApi.listSimple(),
          roleApi.list(),
        ]);
        const wts = (wtRes.data.data || []).filter(wt => wt.isEnabled);
        setWorkerTypes(wts);
        const ous = ouRes.data.data || [];
        setOrgUnits(ous);
        setRoles(roleRes.data.data || []);

        // Auto-assign if only one org unit
        if (ous.length === 1 && !isEditing) {
          setSelectedOrgUnitIds([ous[0].id]);
          setSelectedPrimaryOrgId(ous[0].id);
        }
      } catch (e) {
        // silent
      }
    };
    loadLookups();
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) {
      const loadData = async () => {
        try {
          const workerRes = await workerApi.getById(workerId);
          const w = workerRes.data.data;
          reset({
            fullName: w.fullName || '',
            workerCode: w.workerCode || '',
            phone: w.phone || '',
            email: w.email || '',
            notes: w.notes || '',
          });
          if (w.countryCode) setCountryCode(w.countryCode);
          if (w.avatarUrl) setAvatarPreview(w.avatarUrl);
          setSelectedWorkerTypeId(w.workerTypeId || null);
          setSelectedEmploymentType(w.employmentType || null);
          setSelectedPrimaryOrgId(w.primaryOrgUnitId || null);
          setHasExistingUser(!!w.userId);
          if (w.assignedOrgUnits && w.assignedOrgUnits.length > 0) {
            setSelectedOrgUnitIds(w.assignedOrgUnits.map(o => o.id));
          }
        } catch (e) {
          setSnackbar(e.response?.data?.message || t('common.error'));
        }
      };
      loadData();
    }
  }, [isEditing, workerId, reset, t]);

  // Auto-set primary when only one org selected
  useEffect(() => {
    if (selectedOrgUnitIds.length === 1) {
      setSelectedPrimaryOrgId(selectedOrgUnitIds[0]);
    } else if (selectedOrgUnitIds.length === 0) {
      setSelectedPrimaryOrgId(null);
    } else if (selectedPrimaryOrgId && !selectedOrgUnitIds.includes(selectedPrimaryOrgId)) {
      setSelectedPrimaryOrgId(null);
    }
  }, [selectedOrgUnitIds, selectedPrimaryOrgId]);

  const pickAvatar = useCallback(async () => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 200,
      maxHeight: 200,
      quality: 0.8,
    });
    if (!result.didCancel && result.assets?.[0]) {
      setAvatarImage(result.assets[0]);
      setAvatarPreview(result.assets[0].uri);
    }
  }, []);

  const onSubmit = useCallback(async (data, addAnother = false) => {
    if (!selectedWorkerTypeId) {
      setSnackbar(t('worker.workerTypeRequired'));
      return;
    }
    if (selectedOrgUnitIds.length === 0) {
      setSnackbar(t('worker.orgUnitRequired'));
      return;
    }
    if (createUserAccount && !hasExistingUser) {
      if (!data.phone || !data.phone.trim()) {
        setSnackbar(t('worker.phoneRequiredForLogin'));
        return;
      }
      if (!password.trim()) {
        setSnackbar(t('worker.passwordRequired'));
        return;
      }
      if (!selectedRoleId) {
        setSnackbar(t('worker.roleRequired'));
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...data,
        countryCode,
        workerTypeId: selectedWorkerTypeId,
        employmentType: selectedEmploymentType,
        primaryOrgUnitId: selectedPrimaryOrgId,
        orgUnitIds: selectedOrgUnitIds,
      };
      if (createUserAccount && !hasExistingUser) {
        payload.createUserAccount = true;
        payload.password = password;
        payload.roleId = selectedRoleId;
      }

      let savedId = workerId;
      if (isEditing) {
        await workerApi.update(workerId, payload);
      } else {
        const res = await workerApi.create(payload);
        savedId = res.data.data.id;
      }
      if (avatarImage) {
        await workerApi.uploadAvatar(savedId, avatarImage);
      }
      if (addAnother) {
        reset({fullName: '', workerCode: '', phone: '', email: '', notes: ''});
        setAvatarImage(null);
        setAvatarPreview(null);
        setSelectedWorkerTypeId(null);
        setSelectedEmploymentType(null);
        setCreateUserAccount(false);
        setPassword('');
        setSelectedRoleId(null);
        setSnackbar(t('worker.savedAddAnother'));
      } else {
        navigation.goBack();
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [isEditing, workerId, navigation, t, avatarImage, selectedWorkerTypeId, selectedEmploymentType, selectedPrimaryOrgId, selectedOrgUnitIds, createUserAccount, hasExistingUser, password, selectedRoleId, reset]);

  const toggleOrgUnit = useCallback((orgId) => {
    setSelectedOrgUnitIds(prev =>
      prev.includes(orgId) ? prev.filter(id => id !== orgId) : [...prev, orgId]
    );
  }, []);

  const selectedWorkerTypeName = workerTypes.find(wt => wt.id === selectedWorkerTypeId);
  const selectedEmpLabel = EMPLOYMENT_TYPES.find(et => et.value === selectedEmploymentType);
  const selectedOrgNames = orgUnits.filter(o => selectedOrgUnitIds.includes(o.id));
  const primaryOrgName = orgUnits.find(o => o.id === selectedPrimaryOrgId);
  const selectedRoleName = roles.find(r => r.id === selectedRoleId);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title={isEditing ? t('worker.editWorker') : t('worker.addWorker')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Avatar */}
        <TouchableOpacity style={styles.avatarPicker} onPress={pickAvatar}>
          {avatarPreview ? (
            <Image source={{uri: avatarPreview}} style={styles.avatarImage} />
          ) : (
            <View style={styles.avatarPlaceholder}>
              <Text style={styles.avatarPlaceholderIcon}>+</Text>
            </View>
          )}
          <View style={styles.cameraOverlay}>
            <Text style={styles.cameraIcon}>&#128247;</Text>
          </View>
        </TouchableOpacity>

        {/* Section 1: Basic Information */}
        <Text variant="titleSmall" style={styles.sectionTitle}>{t('worker.sectionBasic')}</Text>
        <Divider style={styles.divider} />

        <Controller control={control} name="workerCode" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={t('worker.workerCode')} value={value} onChangeText={onChange} onBlur={onBlur}
            mode="outlined" style={styles.input}
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />
        <Controller control={control} name="fullName" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={`${t('worker.name')} *`} value={value} onChangeText={onChange} onBlur={onBlur}
            error={!!errors.fullName} mode="outlined" style={styles.input}
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />
        <Controller control={control} name="phone" render={({field: {onChange, value}}) => (
          <PhoneInput
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            phone={value}
            onPhoneChange={onChange}
            label={t('worker.phone')}
          />
        )} />
        <Controller control={control} name="email" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={t('worker.email')} value={value} onChangeText={onChange} onBlur={onBlur}
            error={!!errors.email} mode="outlined" style={styles.input} keyboardType="email-address"
            autoCapitalize="none"
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />

        {/* Worker Type Selector */}
        <TouchableOpacity activeOpacity={0.7} onPress={() => setWorkerTypeDialogVisible(true)}>
          <View pointerEvents="none">
            <TextInput
              label={`${t('worker.workerType')} *`}
              value={selectedWorkerTypeName ? (selectedWorkerTypeName.displayNameOverride || selectedWorkerTypeName.name) : ''}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="chevron-down" />}
              outlineColor={colors.border}
            />
          </View>
        </TouchableOpacity>

        {/* Employment Type Selector */}
        <TouchableOpacity activeOpacity={0.7} onPress={() => setEmpTypeDialogVisible(true)}>
          <View pointerEvents="none">
            <TextInput
              label={t('worker.employmentType')}
              value={selectedEmpLabel ? selectedEmpLabel.label : ''}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="chevron-down" />}
              outlineColor={colors.border}
            />
          </View>
        </TouchableOpacity>

        {/* Section 2: OrgUnit Assignment */}
        <Text variant="titleSmall" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>{t('worker.sectionOrgUnit')}</Text>
        <Divider style={styles.divider} />

        <TouchableOpacity activeOpacity={0.7} onPress={() => setOrgUnitDialogVisible(true)}>
          <View pointerEvents="none">
            <TextInput
              label={`${t('worker.assignedOrgUnits')} *`}
              value={selectedOrgNames.map(o => o.name).join(', ')}
              mode="outlined"
              style={styles.input}
              editable={false}
              right={<TextInput.Icon icon="chevron-down" />}
              outlineColor={colors.border}
            />
          </View>
        </TouchableOpacity>

        {selectedOrgUnitIds.length > 1 && (
          <TouchableOpacity activeOpacity={0.7} onPress={() => setPrimaryOrgDialogVisible(true)}>
            <View pointerEvents="none">
              <TextInput
                label={`${t('worker.primaryOrgUnit')} *`}
                value={primaryOrgName ? primaryOrgName.name : ''}
                mode="outlined"
                style={styles.input}
                editable={false}
                right={<TextInput.Icon icon="chevron-down" />}
                outlineColor={colors.border}
              />
            </View>
          </TouchableOpacity>
        )}

        {/* Section 3: App Login */}
        <Text variant="titleSmall" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>{t('worker.sectionAppLogin')}</Text>
        <Divider style={styles.divider} />

        {hasExistingUser ? (
          <View style={styles.linkedUserBanner}>
            <Text style={styles.linkedUserText}>{t('worker.userAccountLinked')}</Text>
          </View>
        ) : (
          <>
            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>{t('worker.enableAppLogin')}</Text>
              <Switch
                value={createUserAccount}
                onValueChange={setCreateUserAccount}
                color={colors.primary}
              />
            </View>
            {createUserAccount && (
              <>
                <TextInput
                  label={`${t('worker.loginPassword')} *`}
                  value={password}
                  onChangeText={setPassword}
                  mode="outlined"
                  style={styles.input}
                  secureTextEntry
                  outlineColor={colors.border}
                  activeOutlineColor={colors.primary}
                />
                <TouchableOpacity activeOpacity={0.7} onPress={() => setRoleDialogVisible(true)}>
                  <View pointerEvents="none">
                    <TextInput
                      label={`${t('worker.loginRole')} *`}
                      value={selectedRoleName ? selectedRoleName.name : ''}
                      mode="outlined"
                      style={styles.input}
                      editable={false}
                      right={<TextInput.Icon icon="chevron-down" />}
                      outlineColor={colors.border}
                    />
                  </View>
                </TouchableOpacity>
                <Text style={styles.loginHint}>{t('worker.loginHint')}</Text>
              </>
            )}
          </>
        )}

        {/* Section 4: Additional Notes */}
        <Text variant="titleSmall" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>{t('worker.sectionNotes')}</Text>
        <Divider style={styles.divider} />

        <Controller control={control} name="notes" render={({field: {onChange, onBlur, value}}) => (
          <TextInput label={t('worker.notes')} value={value} onChangeText={onChange} onBlur={onBlur}
            mode="outlined" style={[styles.input, {height: 80}]} multiline numberOfLines={3}
            outlineColor={colors.border} activeOutlineColor={colors.primary} />
        )} />

        {/* Validation errors */}
        {Object.keys(errors).length > 0 && (
          <Text style={styles.errorText}>{errors[Object.keys(errors)[0]]?.message}</Text>
        )}

        {/* Actions */}
        <View style={styles.buttonRow}>
          <Button mode="contained" onPress={handleSubmit(data => onSubmit(data, false))} loading={saving} disabled={saving}
            style={styles.saveButton} buttonColor={colors.primary}>
            {t('common.save')}
          </Button>
          {!isEditing && (
            <Button mode="outlined" onPress={handleSubmit(data => onSubmit(data, true))} disabled={saving}
              style={styles.saveAnotherButton} textColor={colors.primary}>
              {t('worker.saveAndAddAnother')}
            </Button>
          )}
        </View>
        <View style={{height: 40}} />
      </ScrollView>

      {/* Worker Type Dialog */}
      <Portal>
        <Dialog visible={workerTypeDialogVisible} onDismiss={() => setWorkerTypeDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.selectWorkerType')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {workerTypes.map(wt => (
                <Chip
                  key={wt.id}
                  selected={selectedWorkerTypeId === wt.id}
                  onPress={() => { setSelectedWorkerTypeId(wt.id); setWorkerTypeDialogVisible(false); }}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {wt.displayNameOverride || wt.name}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => { setSelectedWorkerTypeId(null); setWorkerTypeDialogVisible(false); }}>
              {t('common.none')}
            </Button>
            <Button onPress={() => setWorkerTypeDialogVisible(false)}>{t('common.cancel')}</Button>
          </Dialog.Actions>
        </Dialog>

        {/* Employment Type Dialog */}
        <Dialog visible={empTypeDialogVisible} onDismiss={() => setEmpTypeDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.selectEmploymentType')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {EMPLOYMENT_TYPES.map(et => (
                <Chip
                  key={et.value}
                  selected={selectedEmploymentType === et.value}
                  onPress={() => { setSelectedEmploymentType(et.value); setEmpTypeDialogVisible(false); }}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {et.label}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => { setSelectedEmploymentType(null); setEmpTypeDialogVisible(false); }}>
              {t('common.none')}
            </Button>
            <Button onPress={() => setEmpTypeDialogVisible(false)}>{t('common.cancel')}</Button>
          </Dialog.Actions>
        </Dialog>

        {/* OrgUnit Multi-Select Dialog */}
        <Dialog visible={orgUnitDialogVisible} onDismiss={() => setOrgUnitDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.selectOrgUnits')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {orgUnits.map(ou => (
                <Chip
                  key={ou.id}
                  selected={selectedOrgUnitIds.includes(ou.id)}
                  onPress={() => toggleOrgUnit(ou.id)}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {ou.name}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setOrgUnitDialogVisible(false)}>{t('common.done')}</Button>
          </Dialog.Actions>
        </Dialog>

        {/* Primary OrgUnit Dialog */}
        <Dialog visible={primaryOrgDialogVisible} onDismiss={() => setPrimaryOrgDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.selectPrimaryOrg')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {orgUnits.filter(o => selectedOrgUnitIds.includes(o.id)).map(ou => (
                <Chip
                  key={ou.id}
                  selected={selectedPrimaryOrgId === ou.id}
                  onPress={() => { setSelectedPrimaryOrgId(ou.id); setPrimaryOrgDialogVisible(false); }}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {ou.name}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setPrimaryOrgDialogVisible(false)}>{t('common.cancel')}</Button>
          </Dialog.Actions>
        </Dialog>

        {/* Role Dialog */}
        <Dialog visible={roleDialogVisible} onDismiss={() => setRoleDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.selectRole')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 300, paddingHorizontal: 0}}>
            <View style={styles.dialogChipGroup}>
              {roles.map(r => (
                <Chip
                  key={r.id}
                  selected={selectedRoleId === r.id}
                  onPress={() => { setSelectedRoleId(r.id); setRoleDialogVisible(false); }}
                  style={styles.dialogChip}
                  textStyle={{fontSize: 14}}>
                  {r.name}
                </Chip>
              ))}
            </View>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setRoleDialogVisible(false)}>{t('common.cancel')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  avatarPicker: {
    width: 100, height: 100, borderRadius: 50, alignSelf: 'center',
    marginBottom: spacing.md, position: 'relative',
  },
  avatarImage: {width: 100, height: 100, borderRadius: 50},
  avatarPlaceholder: {
    width: 100, height: 100, borderRadius: 50, backgroundColor: colors.secondary,
    justifyContent: 'center', alignItems: 'center',
  },
  avatarPlaceholderIcon: {fontSize: 32, color: colors.white},
  cameraOverlay: {
    position: 'absolute', bottom: 0, right: 0,
    width: 32, height: 32, borderRadius: 16, backgroundColor: colors.primary,
    justifyContent: 'center', alignItems: 'center',
    borderWidth: 2, borderColor: colors.white,
  },
  cameraIcon: {fontSize: 16},
  form: {padding: spacing.md},
  sectionTitle: {fontWeight: '700', color: colors.primary, marginTop: spacing.sm, marginBottom: spacing.xs},
  divider: {marginBottom: spacing.sm},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  errorText: {color: colors.error, marginBottom: spacing.sm, fontSize: 12},
  buttonRow: {marginTop: spacing.lg, gap: spacing.sm},
  saveButton: {borderRadius: 8},
  saveAnotherButton: {borderRadius: 8, borderColor: colors.primary},
  switchRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: colors.surface, padding: spacing.md, borderRadius: 8,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: colors.border,
  },
  switchLabel: {fontSize: 15, color: colors.text},
  linkedUserBanner: {
    backgroundColor: '#E8F5E9', padding: spacing.md, borderRadius: 8,
    marginBottom: spacing.sm,
  },
  linkedUserText: {color: '#2E7D32', fontSize: 14},
  loginHint: {fontSize: 12, color: colors.textLight, marginBottom: spacing.sm, marginTop: -4},
  dialogChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, padding: spacing.lg},
  dialogChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
});

export default WorkerFormScreen;
